/**
 * URL Parameter Utilities for Deep Linking & Dashboard State
 * Handles parsing, validation, and management of query parameters
 * for the AlertsTab and general dashboard navigation.
 */

export type FilterOption = "all" | "pending" | "approved" | "rejected";
export type SortOption = "adherence_asc" | "date_desc" | "participant_asc";

export interface AlertsURLParams {
  study_id?: string;
  filter?: FilterOption;
  sort?: SortOption;
  highlight?: string[]; // comma-separated participant IDs
  notification_ref?: string; // execution_id for tracking
  start_date?: string; // ISO date YYYY-MM-DD
  end_date?: string; // ISO date YYYY-MM-DD
}

/**
 * Parse query parameters from current URL
 */
export function parseAlertsURLParams(): AlertsURLParams {
  const params = new URLSearchParams(window.location.search);

  const highlightStr = params.get("highlight");
  const highlight = highlightStr
    ? highlightStr
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean)
    : [];

  return {
    study_id: params.get("study_id") || undefined,
    filter: (params.get("filter") as FilterOption) || undefined,
    sort: (params.get("sort") as SortOption) || undefined,
    highlight,
    notification_ref: params.get("notification_ref") || undefined,
    start_date: params.get("start_date") || undefined,
    end_date: params.get("end_date") || undefined,
  };
}

/**
 * Build query string from params
 */
export function buildAlertsQueryString(params: AlertsURLParams): string {
  const searchParams = new URLSearchParams();

  if (params.study_id) searchParams.set("study_id", params.study_id);
  if (params.filter) searchParams.set("filter", params.filter);
  if (params.sort) searchParams.set("sort", params.sort);
  if (params.highlight && params.highlight.length > 0) {
    searchParams.set("highlight", params.highlight.join(","));
  }
  if (params.notification_ref)
    searchParams.set("notification_ref", params.notification_ref);
  if (params.start_date) searchParams.set("start_date", params.start_date);
  if (params.end_date) searchParams.set("end_date", params.end_date);

  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

/**
 * Update URL without page reload using history API
 */
export function updateAlertsURL(params: AlertsURLParams): void {
  const queryString = buildAlertsQueryString(params);
  const newURL = `/dashboard/alerts${queryString}`;
  window.history.replaceState({}, "", newURL);
}

/**
 * Validate filter option
 */
export function isValidFilter(filter: any): filter is FilterOption {
  return ["all", "pending", "approved", "rejected"].includes(filter);
}

/**
 * Validate sort option
 */
export function isValidSort(sort: any): sort is SortOption {
  return ["adherence_asc", "date_desc", "participant_asc"].includes(sort);
}

/**
 * Validate ISO date format (YYYY-MM-DD)
 */
export function isValidISODate(dateStr: any): boolean {
  if (typeof dateStr !== "string") return false;
  const iso8601Regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!iso8601Regex.test(dateStr)) return false;
  const date = new Date(`${dateStr}T00:00:00Z`);
  return !isNaN(date.getTime());
}

// ---------------------------------------------------------------------------
// General Dashboard URL Params (coexists with AlertsURLParams)
// ---------------------------------------------------------------------------

export interface DashboardURLParams {
  tab?: number;
  sort_col?: string;
  sort_dir?: "asc" | "desc";
}

/**
 * Parse general dashboard params from current URL.
 * Reads only its own keys so it coexists with alerts params.
 */
export function parseDashboardURLParams(): DashboardURLParams {
  const params = new URLSearchParams(window.location.search);

  const tabStr = params.get("tab");
  const tab = tabStr !== null ? parseInt(tabStr, 10) : undefined;

  return {
    tab: tab !== undefined && !isNaN(tab) ? tab : undefined,
    sort_col: params.get("sort_col") || undefined,
    sort_dir:
      params.get("sort_dir") === "asc" || params.get("sort_dir") === "desc"
        ? (params.get("sort_dir") as "asc" | "desc")
        : undefined,
  };
}

/**
 * Update dashboard URL params without page reload.
 * Merges into existing search params so alerts params are preserved.
 */
export function updateDashboardURL(params: DashboardURLParams): void {
  const searchParams = new URLSearchParams(window.location.search);

  // Set or delete each dashboard key
  if (params.tab !== undefined) {
    searchParams.set("tab", String(params.tab));
  } else {
    searchParams.delete("tab");
  }

  if (params.sort_col) {
    searchParams.set("sort_col", params.sort_col);
  } else {
    searchParams.delete("sort_col");
  }

  if (params.sort_dir) {
    searchParams.set("sort_dir", params.sort_dir);
  } else {
    searchParams.delete("sort_dir");
  }

  const query = searchParams.toString();
  const newURL = `${window.location.pathname}${query ? `?${query}` : ""}`;
  window.history.replaceState({}, "", newURL);
}

// ---------------------------------------------------------------------------
// Session-scoped dashboard state (ephemeral, not shareable)
// ---------------------------------------------------------------------------

export interface DashboardSessionState {
  scrollPosition?: number;
  expandedGroups?: string[];
  filtersExpanded?: boolean;
  showAllRows?: boolean;
  gcsBucketDismissed?: boolean;
  // Data extraction config preservation
  extractionConfig?: {
    dataSource?: string;
    dateRange?: { startDate?: string; endDate?: string; mode?: string };
    selectedParticipants?: string[];
    selectedDataTypes?: string[];
    expandedAccordions?: string[];
  };
}

const SESSION_KEY_PREFIX = "studysync_dashboard_";

/**
 * Save ephemeral dashboard state to sessionStorage.
 */
export function saveDashboardSession(
  studyId: string,
  state: Partial<DashboardSessionState>
): void {
  try {
    const key = `${SESSION_KEY_PREFIX}${studyId}`;
    const existing = loadDashboardSession(studyId);
    const merged = { ...existing, ...state };
    sessionStorage.setItem(key, JSON.stringify(merged));
  } catch {
    // sessionStorage may be unavailable; silently ignore
  }
}

/**
 * Load ephemeral dashboard state from sessionStorage.
 */
export function loadDashboardSession(
  studyId: string
): DashboardSessionState | null {
  try {
    const key = `${SESSION_KEY_PREFIX}${studyId}`;
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as DashboardSessionState) : null;
  } catch {
    return null;
  }
}

/**
 * Clear ephemeral dashboard state for a study.
 */
export function clearDashboardSession(studyId: string): void {
  try {
    sessionStorage.removeItem(`${SESSION_KEY_PREFIX}${studyId}`);
  } catch {
    // silently ignore
  }
}

/**
 * Create notification banner props from URL params
 */
export function createNotificationBannerProps(
  params: AlertsURLParams,
  highlightedCount: number,
) {
  if (!params.notification_ref || highlightedCount === 0) {
    return null;
  }

  return {
    executionId: params.notification_ref,
    participantCount: highlightedCount,
  };
}
